/**
 * ORDER 23 range-chart structure model.
 *
 * All decisions are point-in-time over already-closed served bars. Boxes use candle BODIES;
 * wicks remain display provenance and must never widen the structure. The measured thresholds
 * are named here so CHART C can render the evidence without re-deriving it in a component.
 */

export const BOX_BODY_WIDTH_MULTIPLIER = 2.5;
export const BREAK_DISTANCE_MULTIPLIER = .5;
export const BOX_DURATION_GATE_MINUTES = 35;
export const SLOW_BREAK_MINUTES = 5;
export const FAST_BREAK_MAX_MINUTES = 2;
export const DELTA_PUSH_REVERSAL_CONTRACTS = 400;
export const DELTA_PUSH_MIN_CONTRACTS = 1_500;
export const DELTA_FLIP_RATIO = .25;
export const DELTA_FLIP_SEARCH_BARS = 60;
export const STUDY_SESSION_OFFSET_MS = 2 * 60 * 60_000;

export interface RangeStudySession<T> {
  readonly sessionDate: string;
  readonly bars: T[];
}

export interface RangeBoxBar {
  readonly t0: number;
  readonly t1: number;
  readonly minutes: number;
  readonly o: number;
  readonly h: number;
  readonly l: number;
  readonly c: number;
  readonly contracts: number;
  readonly max_bar: number;
  /** Index of the source time bar whose close completed this range bar. */
  readonly sourceIndex?: number;
}

export interface RangeBoxOptions {
  readonly boxBars?: number;
  readonly rangeTicks?: number;
  readonly tickSize?: number;
}

export interface RangeBox {
  readonly startIndex: number;
  readonly endIndex: number;
  readonly lower: number;
  readonly upper: number;
  readonly widthTicks: number;
  /** Knowledge-time duration used by gates and labels: last close minus first open. */
  readonly minutes: number;
  /** Legacy study's sum of rounded/inclusive bar minutes, retained only for oracle parity. */
  readonly studyMinutes: number;
  readonly contracts: number;
}

export interface RangeBreakGates {
  readonly duration: boolean;
  readonly speed: boolean;
  readonly deltaAgreement: boolean;
  readonly barDeltaAgreement: boolean;
}

export interface RangeBreak {
  readonly box: RangeBox;
  readonly breakIndex: number;
  readonly breakAt: number;
  readonly direction: "up" | "down";
  readonly breakMinutes: number;
  readonly breakContracts: number;
  readonly breakDeltaAgrees: boolean;
  readonly speed: "fast" | "middle" | "slow";
  readonly gates: RangeBreakGates;
  readonly gatesPassed: number;
}

interface ResolvedOptions {
  boxBars: number;
  rangeTicks: number;
  tickSize: number;
}

interface ScanResult {
  breaks: RangeBreak[];
  activeBox: RangeBox | null;
}

function resolvedOptions(options: RangeBoxOptions): ResolvedOptions {
  const boxBars = options.boxBars ?? 6;
  const rangeTicks = options.rangeTicks ?? 8;
  const tickSize = options.tickSize ?? .25;
  if (!Number.isInteger(boxBars) || boxBars < 2) throw new Error("range box K must be an integer >= 2");
  if (!Number.isInteger(rangeTicks) || rangeTicks < 1) throw new Error("range ticks must be a positive integer");
  if (!Number.isFinite(tickSize) || tickSize <= 0) throw new Error("range tick size must be positive");
  return { boxBars, rangeTicks, tickSize };
}

function assertBars(bars: readonly RangeBoxBar[]): void {
  for (const [index, bar] of bars.entries()) {
    for (const field of ["t0", "t1", "minutes", "o", "h", "l", "c", "contracts", "max_bar"] as const) {
      if (!Number.isFinite(bar[field])) throw new Error(`range bar ${index} has non-finite ${field}`);
    }
    if (bar.t1 < bar.t0) throw new Error(`range bar ${index} closes before it opens`);
    if (bar.minutes < 0) throw new Error(`range bar ${index} has negative minutes`);
  }
}

export function studySessionDate(timestamp: number): string {
  return new Date(timestamp + STUDY_SESSION_OFFSET_MS).toISOString().slice(0, 10);
}

/** Partition an ordered served range tape into contiguous calibrated study sessions. */
export function partitionRangeBarsByStudySession<T extends { readonly t1: number }>(
  bars: readonly T[],
): RangeStudySession<T>[] {
  const sessions: RangeStudySession<T>[] = [];
  for (const bar of bars) {
    const sessionDate = studySessionDate(bar.t1);
    const current = sessions.at(-1);
    if (current?.sessionDate === sessionDate) current.bars.push(bar);
    else sessions.push({ sessionDate, bars: [bar] });
  }
  return sessions;
}

function assertSingleStudySession(bars: readonly { t1: number }[], label: string): void {
  if (!bars.length) return;
  const session = studySessionDate(bars[0].t1);
  if (bars.some((bar) => studySessionDate(bar.t1) !== session))
    throw new Error(`${label} must belong to one calibrated session`);
}

function bodyBounds(bars: readonly RangeBoxBar[], start: number, endExclusive: number) {
  let lower = Number.POSITIVE_INFINITY;
  let upper = Number.NEGATIVE_INFINITY;
  for (let index = start; index < endExclusive; index += 1) {
    lower = Math.min(lower, bars[index].o, bars[index].c);
    upper = Math.max(upper, bars[index].o, bars[index].c);
  }
  return { lower, upper };
}

function makeBox(
  bars: readonly RangeBoxBar[],
  startIndex: number,
  endIndex: number,
  lower: number,
  upper: number,
  tickSize: number,
): RangeBox {
  return {
    startIndex,
    endIndex,
    lower,
    upper,
    widthTicks: (upper - lower) / tickSize,
    minutes: elapsedMinutes(bars[startIndex].t0, bars[endIndex].t1),
    studyMinutes: bars.slice(startIndex, endIndex + 1).reduce((sum, bar) => sum + bar.minutes, 0),
    contracts: bars.slice(startIndex, endIndex + 1).reduce((sum, bar) => sum + bar.contracts, 0),
  };
}

function scanRangeStructures(bars: readonly RangeBoxBar[], options: RangeBoxOptions = {}): ScanResult {
  const { boxBars, rangeTicks, tickSize } = resolvedOptions(options);
  assertBars(bars);
  assertSingleStudySession(bars, "range bars");
  const breaks: RangeBreak[] = [];
  let activeBox: RangeBox | null = null;
  let cursor = boxBars;

  while (cursor <= bars.length) {
    const startIndex = cursor - boxBars;
    const { lower, upper } = bodyBounds(bars, startIndex, cursor);
    const widthTicks = (upper - lower) / tickSize;
    if (widthTicks > BOX_BODY_WIDTH_MULTIPLIER * rangeTicks) {
      cursor += 1;
      continue;
    }

    const allowance = rangeTicks * tickSize * BREAK_DISTANCE_MULTIPLIER;
    let breakIndex = cursor;
    // Preserve the checked-in study's inclusive allowance line so its 147-event oracle remains exact.
    while (
      breakIndex < bars.length &&
      bars[breakIndex].c >= lower - allowance &&
      bars[breakIndex].c <= upper + allowance
    ) breakIndex += 1;

    const box = makeBox(bars, startIndex, breakIndex - 1, lower, upper, tickSize);
    if (breakIndex === bars.length) {
      activeBox = box;
      break;
    }

    const breakBar = bars[breakIndex];
    const direction: RangeBreak["direction"] = breakBar.c > upper ? "up" : "down";
    const directionSign = direction === "up" ? 1 : -1;
    // Study parity: zero net is "not positive", so it agrees with a down break in the checked-in
    // oracle. Preserve that exact classification instead of inventing a third sign here.
    const deltaAgreement = (direction === "up") === (box.contracts > 0);
    const barDeltaAgreement = breakBar.contracts !== 0 && Math.sign(breakBar.contracts) === directionSign;
    const gates: RangeBreakGates = {
      duration: box.minutes >= BOX_DURATION_GATE_MINUTES,
      speed: breakBar.minutes >= SLOW_BREAK_MINUTES,
      deltaAgreement,
      barDeltaAgreement,
    };
    const speed: RangeBreak["speed"] = breakBar.minutes <= FAST_BREAK_MAX_MINUTES
      ? "fast"
      : breakBar.minutes >= SLOW_BREAK_MINUTES ? "slow" : "middle";
    breaks.push({
      box,
      breakIndex,
      breakAt: breakBar.t1,
      direction,
      breakMinutes: breakBar.minutes,
      breakContracts: breakBar.contracts,
      breakDeltaAgrees: barDeltaAgreement,
      speed,
      gates,
      gatesPassed: Number(gates.duration) + Number(gates.speed) +
        Number(gates.deltaAgreement) + Number(gates.barDeltaAgreement),
    });
    cursor = breakIndex + 1;
  }

  return { breaks, activeBox };
}

/** Detect completed boxes. A break is timestamped at the outside bar's close (`t1`), never its open. */
export function detectRangeBreaks(
  bars: readonly RangeBoxBar[],
  options: RangeBoxOptions = {},
): RangeBreak[] {
  return scanRangeStructures(bars, options).breaks;
}

/** Return the final unbroken box so CHART C can extend its rectangle live. */
export function findActiveRangeBox(
  bars: readonly RangeBoxBar[],
  options: RangeBoxOptions = {},
): RangeBox | null {
  return scanRangeStructures(bars, options).activeBox;
}

/** Real elapsed minutes for the range-minute label; no inclusive bucket counting. */
export function elapsedMinutes(t0: number, t1: number): number {
  if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 < t0)
    throw new Error("range elapsed time requires finite ordered timestamps");
  return (t1 - t0) / 60_000;
}

export interface TimeDeltaBar {
  readonly t0: number;
  readonly t1: number;
  readonly delta: number;
  readonly cvd: number;
  readonly close: number;
}

export interface DeltaFlip {
  readonly t: number;
  /** Earliest receipt time at which the completed push and flip are both known. */
  readonly confirmedAt: number;
  readonly pushStart: number;
  readonly pushEnd: number;
  readonly pushContracts: number;
  readonly pushMaxBar: number;
  readonly flipContracts: number;
  readonly pushSide: "up" | "down";
  readonly side: "up" | "down";
  readonly lagBars: number;
}

function assertTimeBars(bars: readonly TimeDeltaBar[]): void {
  for (const [index, bar] of bars.entries()) {
    for (const field of ["t0", "t1", "delta", "cvd", "close"] as const)
      if (!Number.isFinite(bar[field])) throw new Error(`time delta bar ${index} has non-finite ${field}`);
    if (bar.t1 < bar.t0) throw new Error(`time delta bar ${index} closes before it opens`);
  }
  assertSingleStudySession(bars, "time delta bars");
}

interface DeltaPush {
  start: number;
  extreme: number;
  detected: number;
  contracts: number;
  direction: 1 | -1;
}

function detectPushes(bars: readonly TimeDeltaBar[]): DeltaPush[] {
  if (bars.length < 2) return [];
  const pushes: DeltaPush[] = [];
  let start = 0;
  let extreme = 0;
  let direction: -1 | 0 | 1 = 0;
  for (let index = 1; index < bars.length; index += 1) {
    if (direction === 0) {
      if (bars[index].cvd - bars[start].cvd >= DELTA_PUSH_REVERSAL_CONTRACTS) {
        direction = 1;
        extreme = index;
      } else if (bars[start].cvd - bars[index].cvd >= DELTA_PUSH_REVERSAL_CONTRACTS) {
        direction = -1;
        extreme = index;
      }
      continue;
    }
    if (direction === 1) {
      if (bars[index].cvd > bars[extreme].cvd) extreme = index;
      else if (bars[extreme].cvd - bars[index].cvd >= DELTA_PUSH_REVERSAL_CONTRACTS) {
        pushes.push({ start, extreme, detected: index, contracts: bars[extreme].cvd - bars[start].cvd, direction });
        start = extreme;
        extreme = index;
        direction = -1;
      }
    } else if (bars[index].cvd < bars[extreme].cvd) extreme = index;
    else if (bars[index].cvd - bars[extreme].cvd >= DELTA_PUSH_REVERSAL_CONTRACTS) {
      pushes.push({ start, extreme, detected: index, contracts: bars[extreme].cvd - bars[start].cvd, direction });
      start = extreme;
      extreme = index;
      direction = 1;
    }
  }
  return pushes;
}

/** Amendment B Δ↺: first qualifying opposite-sign closed bar after each measured CVD push. */
export function detectDeltaFlips(bars: readonly TimeDeltaBar[]): DeltaFlip[] {
  assertTimeBars(bars);
  const flips: DeltaFlip[] = [];
  for (const push of detectPushes(bars)) {
    if (Math.abs(push.contracts) < DELTA_PUSH_MIN_CONTRACTS) continue;
    const pushMaxBar = Math.max(...bars.slice(push.start, push.extreme + 1).map((bar) => Math.abs(bar.delta)));
    const end = Math.min(push.extreme + DELTA_FLIP_SEARCH_BARS, bars.length);
    for (let index = push.extreme + 1; index < end; index += 1) {
      if (bars[index].delta * push.direction >= 0 || Math.abs(bars[index].delta) < pushMaxBar * DELTA_FLIP_RATIO)
        continue;
      flips.push({
        t: bars[index].t1,
        confirmedAt: Math.max(bars[index].t1, bars[push.detected].t1),
        pushStart: bars[push.start].t0,
        pushEnd: bars[push.extreme].t1,
        pushContracts: push.contracts,
        pushMaxBar,
        flipContracts: bars[index].delta,
        pushSide: push.direction === 1 ? "up" : "down",
        side: push.direction === 1 ? "down" : "up",
        lagBars: index - push.extreme,
      });
      break;
    }
  }
  return flips;
}

export interface RangeAbsorbedMarker {
  readonly index: number;
  readonly t: number;
  readonly contracts: number;
  readonly threshold: number;
  readonly thresholdKnownAt: number;
  readonly thresholdBasis: string;
  readonly side: "up" | "down";
}

export interface RangeAbsorbedThreshold {
  /** Pre-registered absolute-contract threshold. */
  readonly contracts: number;
  /** Knowledge timestamp proving the threshold existed before the evaluated tape. */
  readonly knownAt: number;
  readonly basis: string;
}

/** Amendment B ⊠: pre-registered p90-or-larger aggressor delta against the range bar body. */
export function detectRangeAbsorbed(
  bars: readonly RangeBoxBar[],
  threshold: RangeAbsorbedThreshold,
): RangeAbsorbedMarker[] {
  assertBars(bars);
  if (!Number.isFinite(threshold.contracts) || threshold.contracts < 0)
    throw new Error("range absorbed threshold contracts must be non-negative");
  if (!Number.isFinite(threshold.knownAt)) throw new Error("range absorbed threshold knownAt must be finite");
  if (!threshold.basis.trim()) throw new Error("range absorbed threshold basis must be non-empty");
  const firstClose = bars.reduce((earliest, bar) => Math.min(earliest, bar.t1), Number.POSITIVE_INFINITY);
  if (threshold.knownAt >= firstClose)
    throw new Error("range absorbed threshold was not known before the tape; it must be strictly before the first close");
  return bars.flatMap((bar, index) => {
    const direction = Math.sign(bar.c - bar.o);
    if (
      direction === 0 ||
      Math.abs(bar.contracts) < threshold.contracts ||
      Math.sign(bar.contracts) === direction
    ) return [];
    return [{
      index,
      t: bar.t1,
      contracts: bar.contracts,
      threshold: threshold.contracts,
      thresholdKnownAt: threshold.knownAt,
      thresholdBasis: threshold.basis,
      side: direction === 1 ? "up" as const : "down" as const,
    }];
  });
}
